// Libaries
import clsx from "clsx";

// React
import { MutableRefObject, RefObject } from "react";

// MUI
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridApi, GridCellParams, GridColDef, GridRenderCellParams, GridTreeNode, GridValueGetter } from "@mui/x-data-grid-pro";
import { GridFilterOperator, GridRowId, GridRowIdToModelLookup, GridValidRowModel } from "@mui/x-data-grid-premium";
import { Box, SxProps, Tooltip, Typography } from "@mui/material";
import { GridPipeProcessor, unwrapPrivateAPI } from "@mui/x-data-grid-pro/internals";

// Tables
import { FOOTER_PARAM_ID, GROUP_NODE_ROOT_ID, HEADER_CLASS_NAME, ONE_HUNDRED_TWENTY } from "@/Constants/tables";
import { computeProps, getCellClassNameFn } from "@/Common/helpers/Tables";

// Rendering Components
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyCellProps } from "@/Layouts/components/Tables/DataSmartTable/types";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import { ClassNameHints } from "@Common/types/tables";

export const isAutoGeneratedFooter = (id: string | number) => id === FOOTER_PARAM_ID;

export const isGroupChild = (rowNode: GridTreeNode) => rowNode.parent !== GROUP_NODE_ROOT_ID;

export const formatWithParens = (value: string | number) => `(${value})`;

export const getRowId = (row: GridValidRowModel) => row._id;

export const getCellClasses = (defaults: string, givenHints: Partial<ClassNameHints>, cellParams?: GridCellParams) => {
	const hints = { ...computeProps("", cellParams, []), ...givenHints };
	// Allow external override to disable primary footer background via cellParams?.colDef?._disableFooterPrimaryBg flag
	const disablePrimary = (cellParams as any)?.colDef?._disableFooterPrimaryBg;
	return clsx({
		[defaults]: true,
		"!bg-[color:--primary-color] !text-white": !disablePrimary && !hints.isNegative && hints.isFooter,
		"!bg-[color:--light-gray-color-v2] !text-[color:--text-alert-color]": hints.isNegative && hints.isFooter,
		"bg-[#b3b3b3] !text-white !font-montserrat": hints.isSubTotalRow,
		"bg-[color:--light-gray-color-v2]": hints.isSubTotalRow && hints.isNegative,
		"shadow-[inset_0_0_0_1px_#b3b3b3] bg-[#e8e8e8] rounded-[6px]": hints.isPreviousEdited,
		"table-noWrap": hints.noWrap,
	});
};

export const ifFooterValueGetter =
	(footerValue: number | string): GridValueGetter =>
	(value, row, _column, apiRef) => {
		if (isAutoGeneratedFooter(apiRef.current.getRowId(row))) {
			return footerValue;
		}
		return value;
	};

export const generateSx = (
	apiRef: RefObject<GridApi>,
	parameters?: {
		yGap?: string;
	}
): SxProps => {
	if (!apiRef.current) {
		return {};
	}

	const { leftPinnedWidth, viewportOuterSize, minimumSize, hasScrollY, scrollbarSize } = apiRef.current?.state?.dimensions ?? {};
	const scrollbarInnerSize = minimumSize.width + (hasScrollY ? scrollbarSize : 0) - leftPinnedWidth;

	return {
		"& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--horizontal": {
			marginLeft: `${leftPinnedWidth}px`,
			width: `${viewportOuterSize?.width - leftPinnedWidth}px`,
			"& .MuiDataGrid-scrollbarContent": {
				width: `${scrollbarInnerSize}px !important`,
			},
		},
		"&:after": hasScrollY
			? {
					content: "''",
					position: "absolute",
					bottom: parameters?.yGap?.length
						? `calc(-1*var(--DataGrid-scrollbarSize) - ${parameters.yGap})`
						: "calc(-1*var(--DataGrid-scrollbarSize))",
					left: 0,
					width: `${leftPinnedWidth}px`,
					height: "var(--DataGrid-scrollbarSize)",
					backgroundColor: "white",
					zIndex: 100,
			  }
			: {},
	};
};

export const getPinnedCellClassName = (params: GridCellParams, className = "", hints = {}, footerClassName = "") => {
	return getCellClassNameFn(
		[],
		{
			...hints,
		},
		{
			base: "group-[.odd-row]/stripe:bg-[#F7F7F7] group-[.even-row]/stripe:bg-white " + className,
			footer: footerClassName,
		}
	)(params);
};
interface IGenerateRenderingFnConfig {
	customFooterValue: string;
	parentInParens: boolean;
	customFormatFn?: (params: GridRenderCellParams) => string | null;
}

export const generateRenderingFn = (
	otherTypographyCellProps?: Partial<Omit<TypographyCellProps, "params">>,
	config: Partial<IGenerateRenderingFnConfig> = {}
) => {
	return (param: GridRenderCellParams) => {
		let formattedValue = config.customFormatFn ? config.customFormatFn(param) : param.formattedValue;

		if (config.customFooterValue && isAutoGeneratedFooter(param.id)) {
			formattedValue = config.customFooterValue;
		}

		return <TypographyCell params={{ ...param, formattedValue }} {...otherTypographyCellProps} />;
	};
};

export const baseConfig: Partial<GridColDef> = {
	align: "right",
	headerClassName: HEADER_CLASS_NAME,
	renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
	cellClassName: getCellClassNameFn(),
	renderCell: generateRenderingFn(),
	resizable: false,
	type: "string",
};

export const getRowClassName = (params: GridValidRowModel) =>
	clsx({
		"footer !bg-primary [&_.MuiDataGrid-scrollbarFiller]:hover:!bg-primary [&_.MuiDataGrid-scrollbarFiller]:!border-t-primary":
			isAutoGeneratedFooter(params.id),
		"group/stripe": !isAutoGeneratedFooter(params.id),
		"even-row": params.indexRelativeToCurrentPage % 2,
		"odd-row": !(params.indexRelativeToCurrentPage % 2),
	});

export const getRowsFromFilteredLookUp = (filtered: Record<GridRowId, boolean>, rowModels: GridRowIdToModelLookup): unknown[] => {
	const rows: GridRowId[] = [];
	for (const key in filtered) {
		if (filtered[key]) {
			rows.push(key);
		}
	}
	return rows.map((id) => rowModels[id]);
};

export const WithTooltip = (params: GridRenderCellParams) => {
	if (!params.value) {
		return <span />;
	}
	return (
		<Tooltip
			title={
				<Typography className="!font-sans !text-sm" component="span">
					{params.formattedValue ?? params.value}
				</Typography>
			}
			arrow
		>
			<Box>
				<TypographyCell>{params.formattedValue ?? params.value}</TypographyCell>
			</Box>
		</Tooltip>
	);
};

export const getBasicColumnWithTooltip = (value: number | string | undefined, params: GridRenderCellParams, width: number | undefined) => (
	<CustomCell
		value={value ?? params.formattedValue ?? params.value}
		hasTooltip
		maxWith={width ?? params?.colDef?.width ?? ONE_HUNDRED_TWENTY}
		fontSize="12px"
	/>
);

export const getUniformWidth = (width: number, columnSizeRefactor?: number, resizable = false) => ({
	resizable,
	width,
	minWidth: width,
	maxWidth: columnSizeRefactor ? width * columnSizeRefactor : width,
});

type TAggregatedColDef = GridColDef & {
	aggregationWrappedProperties?: { name: string; wrappedValue: GridFilterOperator[]; originalValue: GridFilterOperator[] }[];
	filterOperators: GridFilterOperator[];
};

const CUSTOM_PROCESSOR_ID = "CUSTON_UNWRAP_FILTER_OPERATORS"; // Processor ID

export const addCustomPipeProcessor = (
	apiRef: MutableRefObject<GridApiPremium>,
	clearProcessorRef: MutableRefObject<(() => void) | undefined>
) => {
	if (apiRef.current) {
		const privateApi = unwrapPrivateAPI(apiRef.current);
		const clearProcessor = privateApi?.registerPipeProcessor("hydrateColumns", CUSTOM_PROCESSOR_ID, customPipeProcessor);

		if (!clearProcessorRef.current) {
			clearProcessorRef.current = clearProcessor;
		}

		if (clearProcessorRef.current !== clearProcessor) {
			clearProcessor();
			clearProcessorRef.current = privateApi.registerPipeProcessor("hydrateColumns", CUSTOM_PROCESSOR_ID, customPipeProcessor);
			privateApi.unstable_applyFilters();
		}
	}
};

const customPipeProcessor: GridPipeProcessor<"hydrateColumns"> = (columnsState) => {
	columnsState.orderedFields.forEach((field) => {
		const column = columnsState.lookup[field] as unknown as TAggregatedColDef;
		if (column.aggregationWrappedProperties) {
			const wrappedFilterOperatorsIndex = column.aggregationWrappedProperties.findIndex(
				(property) => property.name === "filterOperators"
			);

			if (wrappedFilterOperatorsIndex > -1) {
				column.filterOperators = column.aggregationWrappedProperties[wrappedFilterOperatorsIndex].originalValue;
				column.aggregationWrappedProperties[wrappedFilterOperatorsIndex].wrappedValue = column.filterOperators;
			}
		}
		columnsState.lookup[field] = column;
	});
	return columnsState;
};

export const augmentColumnDefinitions =
	<T extends string>(extendedConfig: Partial<Record<T, Partial<GridColDef>>>) =>
	(column: GridColDef): GridColDef => {
		const augmentedColumnConfig = extendedConfig[column.field as T];
		return {
			...column,
			...augmentedColumnConfig,
		};
	};

export const defaultCellRenderer: GridColDef["renderCell"] = (params) => {
	return <TypographyCell params={params} />;
};
